/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AfterViewInit, Component, ComponentRef, Input, OnDestroy, ViewChild, ElementRef,
  OnInit
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subscription, throwError } from 'rxjs';
import {
  catchError,
  concatMap, distinctUntilChanged, filter, take, takeUntil, tap
} from 'rxjs/operators';

import {
  Modal,
  ModalCheckboxFunction,
  ModalStep,
  Multimodal,
  ModalBreadcrumb,
  ModalFooterLinkFunction,
  ModalStepInputType
} from 'models/multimodal.model';
import { BaseComponent } from 'shared/base/base.component';
import { DynamicInsertionDirective } from 'shared/directives/dynamic-insertion.directive';
import { ModalService } from 'shared/services/modal.service';
import { MultimodalConfiguratorService } from 'shared/services/multimodal-configurator.service';
import { StoreService } from 'shared/services/store.service';
import { MultimodalService } from 'shared/services/multimodal.service';
import { LoadingService } from 'app/loading.service';

@Component({
  selector: 'app-multimodal',
  templateUrl: './multimodal.component.html',
  styleUrls: ['./multimodal.component.scss']
})
export class MultimodalComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() modals!: Modal[];

  @Input() currentModalId?: string;

  public isLoading = false;

  public currentModal!: Modal;

  public currentStep!: ModalStep;

  currentStepIndex = 1;

  public stepsSequence: ModalStep[] = [];

  public footerCheckboxChecked = false;

  public formValid = false;

  public componentRef!: ComponentRef<any>;

  public formStatusSubscription?: Subscription;

  public form!: UntypedFormGroup;

  public hasHeaderLink!: boolean;

  public hasFooterLink!: boolean;

  public hasFooterLinkFunction!: boolean;

  public footerLinkFunction!: ModalFooterLinkFunction | undefined;

  public hasFooterCheckbox!: boolean;

  public resetMinHeight = false;

  public hideFooter = false;

  public showCancelBtn = false;

  public defaultGoNextButtonText = '';

  public dataFromPreviousStep!: ModalStepInputType;

  breadcrumbs: ModalBreadcrumb[] = [];

  errors?: Error[] | null;

  @ViewChild(DynamicInsertionDirective) insertionPoint!: DynamicInsertionDirective;

  @ViewChild('goForwardButton') goForwardButton!: ElementRef;

  get isForwardButtonDisabled(): boolean {
    if (this.isContentAForm()) {
      return !this.formValid;
    }

    if (this.componentRef) {
      return !this.componentRef.instance.selectedItem;
    }

    return false;
  }

  constructor(
    private modalService: ModalService,
    public bsModalRef: BsModalRef,
    private fb: UntypedFormBuilder,
    private storeService: StoreService,
    private multimodalConfiguratorService: MultimodalConfiguratorService,
    private multimodalService: MultimodalService,
    private router: Router,
    private loadingService: LoadingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.multimodalService.goBack$.subscribe(() => {
      this.goBack();
    });
    this.loadingService.isLoading$.subscribe((val) => {
      this.isLoading = val;
    })
  }

  ngAfterViewInit(): void {
    this.afterViewInit();
  }

  private afterViewInit(): void {
    this.initModal(this.currentModalId || this.modals[0].id);
  }

  ngOnDestroy(): void {
    this.onDestroy();
  }

  private onDestroy(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
    }

    super.ngOnDestroy();
  }

  private initModal(modalId: string): void {
    this.currentModal = this.modals.find((m) => m.id === modalId) as Modal;
    const firstStep: ModalStep = this.currentModal.steps[0];
    this.stepsSequence = [firstStep];
    this.initStep(firstStep);
  }

  private initStep(newStep: ModalStep): void {
    if (!this.hideFooter) {
      this.goForwardButton.nativeElement.blur();
    }

    this.multimodalConfiguratorService.transformStep(newStep, this.dataFromPreviousStep)
      .subscribe((step: ModalStep) => {
        this.currentStep = step;
        this.currentStepIndex = this.currentModal.steps.findIndex((step) => step.id === this.currentStep.id);
        this.multimodalService.setCurrentStepIndex(this.currentStepIndex);
        this.footerCheckboxChecked = false;
        this.componentRef = this.insertionPoint
          .loadChildComponent(step.body.componentType, this.dataFromPreviousStep);

        // reset dataFromPreviousStep after current step is loaded
        if (this.dataFromPreviousStep) {
          this.dataFromPreviousStep = undefined;
        }

        this.handleForm();
        this.handleList();

        this.hasHeaderLink = Boolean(step && step.header && step.header.link);

        this.hasFooterLink = Boolean(step && step.footer && step.footer.link);
        this.hasFooterCheckbox = Boolean(step && step.footer && step.footer.checkbox);
        this.resetMinHeight = Boolean(step && step.body && step.body.resetMinHeight);
        this.hideFooter = Boolean(step && step.footer && step.footer.hideFooter);
        this.showCancelBtn = Boolean(step && step.footer && step.footer.showCancelBtn);

        if ((step && step.footer && step.footer.link && step.footer.link.function)) {
          this.footerLinkFunction = step.footer!.link!.function;
        }

        // breadcrumbs should be initialized after hasFooterCheckbox,
        // because shouldSkipNextStep depends on hasFooterCheckbox
        this.initBreadcrumbs();

        if (this.hasFooterCheckbox) {
          this.form = this.fb.group({
            checkbox: [null]
          });

          const checkBox: UntypedFormControl = this.form.get('checkbox') as UntypedFormControl;

          checkBox.valueChanges.subscribe((checked) => {
            this.footerCheckboxChecked = checked;

            this.initBreadcrumbs();
          });
        }

        if (step && step.class) {
          this.bsModalRef.setClass(step.class);
        }

        this.handleSelect();
      });
  }

  private initBreadcrumbs(): void {
    const breadcrumbs: ModalBreadcrumb[] = [];

    const steps = this.currentModal.steps.filter((step) => step.header && step.header.breadcrumb);
    if (steps.length > 0 && steps.length === this.currentModal.steps.length) {
      const currentStepIndex = steps.findIndex((step) => step.id === this.currentStep.id);

      let i = 0;
      while (i < steps.length) {
        breadcrumbs.push({
          text: '',
          ...steps[i].header!.breadcrumb,
          visited: i <= currentStepIndex
        });
        i += (i === currentStepIndex && this.shouldSkipNextStep()) ? 2 : 1;
      }
    }
    this.breadcrumbs = breadcrumbs;
  }

  private handleForm(): void {
    if (!this.isContentAForm()) {
      return;
    }

    this.defaultGoNextButtonText = 'GENERAL.SAVE';

    // Needed to disable the forward button initially on multistep modals
    this.formValid = false;

    if (this.formStatusSubscription) {
      this.formStatusSubscription.unsubscribe();
    }

    this.formStatusSubscription = this.storeService.getFormReady()
    .pipe(
      // `formReady` is a global store value and can still hold a stale `true`
      // from another form (e.g. when this modal is opened from within an offer).
      // Only proceed once the current step's component has actually built its
      // form — otherwise reading `.valid` on an undefined form throws and kills
      // this subscription, so later status changes never re-enable the button.
      filter((formReady: boolean) => formReady && !!this.componentRef?.instance?.form),
      tap(() => {
        this.formValid = this.componentRef.instance.form.valid;
      }),
      concatMap((): Observable<string> => {
        return this.componentRef.instance.form.statusChanges;
      }),
      distinctUntilChanged(),
      takeUntil(this.destroyed$),
      catchError(error => {
        console.error('Error in form status pipeline:', error);
        return throwError(() => error);
      })
    )
    .subscribe({
      next: (status: any) => {
        this.formValid = (status === 'VALID');
      },
      error: (err) => {
        console.error('Subscription error:', err);
        // Handle the error - maybe set form to invalid
        this.formValid = false;
      }
    });
  }

  private handleList(): void {
    if (!this.isContentAList()) {
      return;
    }

    this.defaultGoNextButtonText = 'GENERAL.CHOOSE';
  }

  private handleSelect(): void {
    if (this.isContentASelect()) {
      this.componentRef.instance.onSelectedItem
        .pipe(take(1))
        .subscribe((selectedItem: any) => {
          this.dataFromPreviousStep = selectedItem;
          this.goForward();
        });
    }
  }

  private shouldApplyCheckboxBehaviour(checkboxFunction: ModalCheckboxFunction): boolean {
    return Boolean(
      this.hasFooterCheckbox
      && this.currentStep!.footer!.checkbox!.function === checkboxFunction
      && this.footerCheckboxChecked
    );
  }

  private shouldSkipNextStep(): boolean {
    return this.shouldApplyCheckboxBehaviour(ModalCheckboxFunction.SKIP_NEXT_STEP);
  }

  private shouldOpenNewModal(): boolean {
    return this.shouldApplyCheckboxBehaviour(ModalCheckboxFunction.OPEN_NEXT_MODAL);
  }

  goBack(): void {
    if (this.currentModal?.id === 'looseArticle-form' && this.currentStep?.id === 'looseArticle-form-s2') {
      this.componentRef.instance.onClose();
    }
    this.errors = null;
    this.stepsSequence = this.stepsSequence.slice(0, -1);
    const newStep: ModalStep = this.stepsSequence.slice(-1)[0];
    this.initStep(newStep);
  }

  goForward(): void {
    this.errors = null;
    if (this.isContentAList() || (this.isContentAForm() && this.formValid) || this.isContentASelect()) {

      this.componentRef.instance.onSubmit()
        .subscribe(() => {
          this.applyModalNavigation();
        }, (err: any) => {
          if (err.graphQLErrors.length && err.graphQLErrors[0].path[0] === 'createCustomer') {
            return;
          }
          if (err.form && err.form.errors) {
            this.handleFormErrors(err.form.errors);
          } else if (err.networkError) {
            this.errors = err.networkError.error.errors;
          } else if (err.message) {
            this.errors = [err];
          } else {
            this.errors = [{ name: 'Error', message: 'Error' } as Error];
          }
          setTimeout(() => {
            const errorElement = document.querySelector(".modal-error");
            errorElement?.scrollIntoView({ behavior: "smooth" });
          }, 1000);
        });

      return;
    }
    this.applyModalNavigation();
  }

  triggerComponentFunction(): void {
    this.componentRef.instance.onFooterLinkFunction();
  }

  private applyModalNavigation(): void {
    const nextStep = this.getNextStep();
    if (nextStep) {
      this.stepsSequence = [...this.stepsSequence, nextStep];
      this.initStep(nextStep);
    } else if (this.shouldOpenNewModal()) {
      this.openNewModal(this.currentStep!.footer!.checkbox!.modalId as string);
    } else {
      this.bsModalRef.hide();
    }
  }

  openNewModal(modalId: string): void {
    this.errors = null;
    const newModal: Modal = this.modals.find((m) => m.id === modalId) as Modal;
    const openInSameContainer = this.currentModal.groupId === newModal!.groupId;

    if (openInSameContainer) {
      this.initModal(modalId);
    } else {
      this.bsModalRef.hide();
      const initialState: Multimodal = { modals: this.modals, currentModalId: modalId };
      this.bsModalRef = this.modalService.show(MultimodalComponent, { initialState });
    }
  }

  getNextStep(): ModalStep | undefined {
    const stepIncrement = this.shouldSkipNextStep() ? 2 : 1;
    const newIndex = this.currentModal.steps.findIndex((step) => step.id === this.currentStep.id) + stepIncrement;

    return (newIndex < this.currentModal.steps.length) ? this.currentModal.steps[newIndex] : undefined;
  }

  isContentAForm(): boolean {
    return this.currentModal && this.componentRef && (
      typeof this.componentRef.instance.onSubmit === 'function'
      && typeof this.componentRef.instance.onItemSelected !== 'function'
      && typeof this.componentRef.instance.onSelectedItem !== 'object'
    );
  }

  isContentAList(): boolean {
    return this.currentModal && this.componentRef && (
      typeof this.componentRef.instance.onSubmit === 'function'
      && typeof this.componentRef.instance.onItemSelected === 'function'
    );
  }

  isContentASelect(): boolean {
    return this.currentModal && this.componentRef && (
      typeof this.componentRef.instance.onSubmit === 'function'
      && typeof this.componentRef.instance.onSelectedItem === 'object'
    );
  }

  displayBack(): boolean {
    return (this.stepsSequence.length > 1 && !this.hasFooterLink);
  }

  displayHeaderBack(): boolean {
    return this.hasHeaderLink && this.stepsSequence.length > 1;
  }

  closeModal(): void {
    if (typeof this.componentRef.instance.onClose === 'function') {
      this.componentRef.instance.onClose();
    }
    this.bsModalRef.hide();
  }

  goToDashboard(): void {
    this.closeModal();
    this.router.navigateByUrl('angebote');
  }

  handleFormErrors(validationErrors: any): void {
    Object.keys(validationErrors).forEach((prop) => {
      const formControl = this.componentRef.instance.form.get(prop);
      if (formControl) {
        formControl.setErrors(validationErrors[prop]);
      }
    });
  }
}
